import { useState } from "react";
import { Copy, Mail, Check, X, Undo, ListPlus } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useQueryClient } from "@tanstack/react-query";
import type { QuoteDetailDto } from "@/lib/quotes";
import { Button } from "@/components/ui/button";
import { QuotePdfLink } from "@/components/quotes/quote-pdf-link";
import { QuoteConvertButton } from "@/components/quotes/quote-convert-button";
import { CreateTaskDialog } from "@/components/tasks/CreateTaskDialog";
import { usePermissions } from "@/hooks/use-permissions";

export interface QuoteTabToolbarProps {
  quote: QuoteDetailDto;
  quoteId: number;
  canConvert: boolean;
  canApprove: boolean;
  onCopy: () => void;
  onEmail: () => void;
  onApprove: () => void;
  onReject: () => void;
  onRecall: () => void;
  copyPending: boolean;
  sendPending: boolean;
  approvalPending: boolean;
}

/**
 * Quote-tab action toolbar mounted above the 3-column grid. Replaces the
 * page-level headerActions cluster (Send / Duplicate) per design — buttons
 * now live inside the Quote tab content. Wires existing handlers; no new
 * mutation logic.
 */
export function QuoteTabToolbar({
  quote,
  quoteId,
  canConvert,
  canApprove,
  onCopy,
  onEmail,
  onApprove,
  onReject,
  onRecall,
  copyPending,
  sendPending,
  approvalPending,
}: QuoteTabToolbarProps) {
  const { has } = usePermissions();
  const queryClient = useQueryClient();
  const [wqOpen, setWqOpen] = useState(false);
  const canAddToWorkQueue = has("tasks:create:admin");
  const isDraft = quote.status === "Draft";
  const isResendable =
    quote.status === "Sent" ||
    (quote.status === "Approved" && quote.convertedToJobId == null);
  const emailVisible = (isDraft || isResendable) && has("quotes:send");

  return (
    <TooltipProvider>
      <div className="flex flex-nowrap items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full h-8 w-8 border-transparent bg-info/15 text-primary hover:bg-info/25"
              onClick={onCopy}
              disabled={copyPending}
            >
              <Copy className="h-4 w-4" />
              <span className="sr-only">Copy Quote</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy Quote</TooltipContent>
        </Tooltip>

        {emailVisible && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="rounded-full h-8 w-8 border-transparent bg-info/15 text-primary hover:bg-info/25"
                onClick={onEmail}
                disabled={sendPending}
              >
                <Mail className="h-4 w-4" />
                <span className="sr-only">{isDraft ? "Send" : "Resend"}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isDraft ? "Email" : "Resend"}</TooltipContent>
          </Tooltip>
        )}

        {canApprove && (quote.status === "Draft" || quote.status === "Sent") && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon" 
                className="rounded-full h-8 w-8 border-slate-300 text-green-600 hover:bg-green-50" 
                onClick={onApprove} 
                disabled={approvalPending}
              >
                <Check className="h-4 w-4" />
                <span className="sr-only">Approve</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Approve</TooltipContent>
          </Tooltip>
        )}

        {canApprove && quote.status === "Sent" && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon" 
                className="rounded-full h-8 w-8 border-slate-300 text-red-600 hover:bg-red-50" 
                onClick={onReject} 
                disabled={approvalPending}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Reject</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reject</TooltipContent>
          </Tooltip>
        )}

        {canApprove && quote.status === "Approved" && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon" 
                className="rounded-full h-8 w-8 border-slate-300 text-orange-600 hover:bg-orange-50" 
                onClick={onRecall} 
                disabled={approvalPending}
              >
                <Undo className="h-4 w-4" />
                <span className="sr-only">Recall</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Recall</TooltipContent>
          </Tooltip>
        )}

        {quote.status !== "Draft" && <QuotePdfLink quoteId={quote.quoteId} />}

        <QuoteConvertButton
          quoteId={quote.quoteId}
          status={quote.status}
          convertedToJobId={quote.convertedToJobId ?? null}
          canConvert={canConvert}
        />

        {canAddToWorkQueue && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="rounded-full h-8 w-8 border-transparent bg-info/15 text-primary hover:bg-info/25"
                onClick={() => setWqOpen(true)}
              >
                <ListPlus className="h-4 w-4" />
                <span className="sr-only">Add to Work Queue</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Add to Work Queue</TooltipContent>
          </Tooltip>
        )}

      <CreateTaskDialog
        open={wqOpen}
        onOpenChange={setWqOpen}
        presetType="Admin"
        presetQuoteId={quoteId}
        onCreated={() => {
          void queryClient.invalidateQueries({
            queryKey: ["task-list", "quote", quoteId],
          });
        }}
      />
      </div>
    </TooltipProvider>
  );
}
